import React, { useState } from 'react';
import { accountingAPI } from '../../services/api';
import { Button } from '../../components/common/Button';
import { PageHeader } from '../../components/layout/PageHeader';
import { FormField, Input } from '../../components/common/FormField';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6'];
const TYPE_LABELS = { sale:'Sales', purchase:'Purchases', expense:'Expenses', quotation:'Quotations', indirect_income:'Other Income' };

export function ReportsPage() {
  const [range, setRange]     = useState({ startDate: format(new Date(new Date().getFullYear(),0,1),'yyyy-MM-dd'), endDate: format(new Date(),'yyyy-MM-dd') });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const r = await accountingAPI.getSummary(range); setSummary(r.data); toast.success('Report generated'); }
    catch { toast.error('Failed to generate'); }
    finally { setLoading(false); }
  };

  const chartData = summary?.summary?.map(s => ({ name: TYPE_LABELS[s._id]||s._id, amount: Math.abs(Math.round(s.total||0)), count: s.count })) || [];
  const pieData   = chartData.filter(d => d.amount > 0);

  return (
    <div className="space-y-5">
      <PageHeader title="Financial Reports" subtitle="Generate profit & loss summaries"/>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-semibold text-gray-800 mb-4 text-sm">Report Parameters</h3>
        <div className="flex gap-4 flex-wrap items-end">
          <FormField label="From Date"><Input type="date" value={range.startDate} onChange={e=>setRange(r=>({...r,startDate:e.target.value}))}/></FormField>
          <FormField label="To Date"><Input type="date" value={range.endDate} onChange={e=>setRange(r=>({...r,endDate:e.target.value}))}/></FormField>
          <Button icon={ChartBarIcon} loading={loading} onClick={generate}>Generate</Button>
        </div>
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-2xl p-5 border border-green-100"><p className="text-xs font-semibold text-green-600 uppercase mb-1">Total Income</p><p className="text-2xl font-bold text-green-700">{'₹'+Number(summary.revenue||0).toLocaleString('en-IN')}</p></div>
            <div className="bg-red-50 rounded-2xl p-5 border border-red-100"><p className="text-xs font-semibold text-red-500 uppercase mb-1">Total Costs</p><p className="text-2xl font-bold text-red-600">{'₹'+Number(summary.costs||0).toLocaleString('en-IN')}</p></div>
            <div className={((summary.netProfit||0)>=0?'bg-indigo-50 border-indigo-100':'bg-red-50 border-red-100')+' rounded-2xl p-5 border'}>
              <p className={'text-xs font-semibold uppercase mb-1 '+((summary.netProfit||0)>=0?'text-indigo-600':'text-red-500')}>Net {(summary.netProfit||0)>=0?'Profit':'Loss'}</p>
              <p className={'text-2xl font-bold '+((summary.netProfit||0)>=0?'text-indigo-700':'text-red-600')}>{'₹'+Math.abs(summary.netProfit||0).toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 text-sm">By Transaction Type</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fontSize:11}}/>
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'₹'+(v/1000).toFixed(0)+'K'}/>
                  <Tooltip formatter={v=>'₹'+Number(v).toLocaleString('en-IN')}/>
                  <Bar dataKey="amount" fill="#6366f1" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 text-sm">Breakdown</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="amount" label={({name,percent})=>name+' '+Math.round(percent*100)+'%'}>
                    {pieData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={v=>'₹'+Number(v).toLocaleString('en-IN')}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">Detailed Breakdown</h3>
              <p className="text-xs text-gray-400 mt-0.5">{range.startDate} to {range.endDate}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400">Category</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400">Transactions</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400">Amount</th>
                </tr>
              </thead>
              <tbody>
                {summary.summary?.map((item,i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-800">{TYPE_LABELS[item._id]||item._id}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{item.count}</td>
                    <td className={'px-5 py-3 text-right font-semibold '+(['sale','indirect_income'].includes(item._id)?'text-green-600':'text-red-500')}>
                      {['sale','indirect_income'].includes(item._id)?'+':'-'}{'₹'+Number(item.total||0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="px-5 py-3 font-bold text-gray-900" colSpan="2">Net {(summary.netProfit||0)>=0?'Profit':'Loss'}</td>
                  <td className={'px-5 py-3 text-right font-bold text-lg '+((summary.netProfit||0)>=0?'text-green-600':'text-red-500')}>
                    {'₹'+Math.abs(summary.netProfit||0).toLocaleString('en-IN')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}